import type { ConsoleThreadListQuery, Thread } from '@central/api';
import { Card, PageHeader, StatusChip, color, font, formatAgo } from '@central/ui';
import { Badge, Button, Checkbox, MultiSelect, Select, TextInput } from '@mantine/core';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useState, type CSSProperties, type FormEvent } from 'react';
import { keys } from '../api';
import { CustomerSelect } from '../components/CustomerSelect';
import { Empty, QueryState } from '../components/parts';
import { ResellerFilter } from '../components/ResellerFilter';
import {
  inboxListQuery,
  inboxViews,
  kindChip,
  listSearchOf,
  parsePriorities,
  priorityOptions,
  viewsFor,
  type InboxListSearch,
  type InboxSearch,
} from '../lib/inbox';
import { muted } from '../lib/labels';
import { useIsReseller } from '../lib/me';
import { useSelection } from '../lib/selection';
import { threadListOptions, useThreadsApi } from '../lib/threads';
import { PriorityChip, TierChip } from './inboxParts';
import { BulkBar, RowCheck } from './InboxBulk';
import { MessageCustomerModal } from './MessageCustomer';
import { TagFilter } from './TicketOrganize';

const tierOptions = [
  { value: '', label: 'Every tier' },
  { value: 'tier1', label: 'Tier 1 · reseller' },
  { value: 'tier2', label: 'Tier 2 · Rethink' },
];

/**
 * Support inbox (docs/design/AdminInbox.dc.html): the full-width ticket list
 * under its views and filters; a row opens the ticket's own page.
 */
export function Inbox() {
  const search = useSearch({ from: '/app/inbox' });
  const navigate = useNavigate({ from: '/inbox' });
  const reseller = useIsReseller();
  const { view, filters, listQuery } = inboxListQuery(search, reseller);
  const listSearch = listSearchOf(search);
  const set = (next: Partial<InboxSearch>) =>
    void navigate({ search: (s) => ({ ...s, ...next }), replace: true });
  const [composing, setComposing] = useState(false);
  const threads = useThreadsApi();
  const openCount = useQuery({
    queryKey: keys.threadCount({ ...inboxViews.open.q, ...filters }),
    queryFn: () => threads.count({ ...inboxViews.open.q, ...filters }),
  });

  return (
    <>
      <PageHeader
        title={reseller ? 'Support inbox' : 'Inbox'}
        subtitle={
          reseller
            ? 'Tier 1 support for your customers; escalate to Rethink when you need us'
            : 'Messages, bug and feature reports, crash reports from every customer'
        }
        actions={
          <Button color="dark" onClick={() => setComposing(true)}>
            Message a customer
          </Button>
        }
      />
      <MessageCustomerModal
        opened={composing}
        customerId={search.customerId}
        onClose={() => setComposing(false)}
        onSent={(d) => {
          setComposing(false);
          void navigate({
            to: '/inbox/$threadId',
            params: { threadId: d.thread.id },
            search: listSearch,
          });
        }}
      />
      <nav
        aria-label="Filter"
        style={{
          display: 'flex',
          gap: 4,
          overflowX: 'auto',
          borderBottom: `1px solid ${color.border}`,
        }}
      >
        {viewsFor(reseller).map((v) => {
          const on = v === view;
          return (
            <Link
              key={v}
              to="/inbox"
              search={(s) => ({ ...s, view: v === 'open' ? undefined : v })}
              aria-current={on ? 'page' : undefined}
              style={{
                minHeight: 40,
                display: 'inline-flex',
                alignItems: 'center',
                flexShrink: 0,
                padding: '0 12px',
                marginBottom: -1,
                whiteSpace: 'nowrap',
                fontSize: 14,
                fontWeight: on ? 600 : 500,
                textDecoration: 'none',
                color: on ? color.text : color.textMuted,
                borderBottom: `2px solid ${on ? color.text : 'transparent'}`,
              }}
            >
              {inboxViews[v].label}
              {v === 'open' && openCount.data && ` · ${openCount.data.threads}`}
            </Link>
          );
        })}
      </nav>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <TicketSearch value={search.q ?? ''} onSearch={(q) => set({ q: q || undefined })} />
        <CustomerSelect
          label="Customer"
          hideLabel
          placeholder="Every customer"
          w={220}
          value={search.customerId ?? ''}
          onChange={(id) => set({ customerId: id || undefined })}
        />
        {!reseller && (
          <ResellerFilter
            value={search.resellerId ?? ''}
            onChange={(id) => set({ resellerId: id || undefined })}
          />
        )}
        {!reseller && (
          <TagFilter value={search.tag ?? ''} onChange={(tag) => set({ tag: tag || undefined })} />
        )}
        <MultiSelect
          aria-label="Priorities"
          size="sm"
          placeholder={search.priority ? undefined : 'Any priority'}
          w={200}
          maw="100%"
          data={priorityOptions}
          value={parsePriorities(search.priority)}
          onChange={(v) => set({ priority: parsePriorities(v.join(',')).join(',') || undefined })}
        />
        <Select
          aria-label="Tier"
          size="sm"
          w={170}
          maw="100%"
          data={tierOptions}
          value={search.tier ?? ''}
          allowDeselect={false}
          onChange={(v) => set({ tier: v === 'tier1' || v === 'tier2' ? v : undefined })}
        />
        <Checkbox
          label="Unread only"
          size="sm"
          checked={!!search.unread}
          onChange={(e) => set({ unread: e.currentTarget.checked || undefined })}
        />
      </div>
      {/* Keyed by the query, so a selection never outlives the list it was made in. */}
      <ThreadList key={JSON.stringify(listQuery)} q={listQuery} listSearch={listSearch} />
    </>
  );
}

/** Search box: a ticket reference, or words in the subject or a message, applied on Enter. */
function TicketSearch({ value, onSearch }: { value: string; onSearch: (q: string) => void }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onSearch(draft.trim());
  };
  return (
    <form
      role="search"
      onSubmit={submit}
      style={{ display: 'flex', gap: 8, alignItems: 'center', flex: '1 1 280px', minWidth: 0 }}
    >
      <TextInput
        aria-label="Search"
        placeholder="GC-1042, subject or message text"
        title="Message text matches every word in one message, internal notes included"
        size="sm"
        style={{ flex: 1, minWidth: 0 }}
        maxLength={100}
        value={draft}
        onChange={(e) => setDraft(e.currentTarget.value)}
      />
      <Button type="submit" variant="default" size="sm">
        Search
      </Button>
      {value && (
        <Button
          variant="subtle"
          size="sm"
          onClick={() => {
            setDraft('');
            onSearch('');
          }}
        >
          Clear
        </Button>
      )}
    </form>
  );
}

/** Ref, subject, customer, assignee and last activity, after the tick box column. */
const rowColumns = '84px minmax(0, 1fr) 180px 140px 96px';

const columnHead: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};

const cell: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

function ThreadList({ q, listSearch }: { q: ConsoleThreadListQuery; listSearch: InboxListSearch }) {
  const threads = useThreadsApi();
  const list = useInfiniteQuery(threadListOptions(threads, q));
  const items = list.data?.pages.flatMap((p) => p.items) ?? [];
  const ticks = useSelection();
  // Bulk changes and the reseller label are Rethink staff's: a reseller works
  // its own customers' threads one at a time.
  const rethink = !useIsReseller();
  return (
    <Card padded={false}>
      {rethink && <BulkBar items={items} selected={ticks.selected} onSelect={ticks.setSelected} />}
      <QueryState isPending={list.isPending} error={list.error}>
        {() =>
          items.length === 0 ? (
            <Empty>No conversations match.</Empty>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 760 }}>
                <div
                  aria-hidden
                  style={{
                    display: 'flex',
                    borderBottom: `1px solid ${color.rowRule}`,
                    ...muted,
                  }}
                >
                  {rethink && <span style={{ width: 40, flexShrink: 0 }} />}
                  <div
                    style={{
                      flex: 1,
                      display: 'grid',
                      gridTemplateColumns: rowColumns,
                      gap: 12,
                      padding: '8px 20px 8px 12px',
                    }}
                  >
                    <span style={columnHead}>Ticket</span>
                    <span style={columnHead}>Subject</span>
                    <span style={columnHead}>Customer</span>
                    <span style={columnHead}>Assignee</span>
                    <span style={{ ...columnHead, textAlign: 'right' }}>Activity</span>
                  </div>
                </div>
                <nav aria-label="Conversations">
                  {items.map((t, i) => (
                    <div
                      key={t.id}
                      style={{
                        display: 'flex',
                        borderTop: i === 0 ? undefined : `1px solid ${color.rowRule}`,
                      }}
                    >
                      {rethink && (
                        <RowCheck
                          t={t}
                          checked={ticks.selected.has(t.id)}
                          onChange={(on) => ticks.toggle(t.id, on)}
                        />
                      )}
                      <ThreadRow t={t} listSearch={listSearch} showReseller={rethink} />
                    </div>
                  ))}
                </nav>
              </div>
            </div>
          )
        }
      </QueryState>
      {list.hasNextPage && (
        <div style={{ padding: '12px 20px' }}>
          <Button
            variant="default"
            size="sm"
            loading={list.isFetchingNextPage}
            onClick={() => void list.fetchNextPage()}
          >
            Load more
          </Button>
        </div>
      )}
    </Card>
  );
}

/** showReseller: Rethink staff see the customer's reseller under its name. */
function ThreadRow({
  t,
  listSearch,
  showReseller,
}: {
  t: Thread;
  listSearch: InboxListSearch;
  showReseller: boolean;
}) {
  const chip = kindChip[t.kind];
  const who =
    t.startedBy === 'system'
      ? 'Auto-sent'
      : t.preview?.authorKind === 'customer' && t.preview.authorName
        ? t.preview.authorName
        : undefined;
  const second = [who, t.preview?.excerpt, ...t.tags.map((g) => `#${g}`)].filter(Boolean);
  return (
    <Link
      to="/inbox/$threadId"
      params={{ threadId: t.id }}
      search={listSearch}
      style={{
        flex: 1,
        minWidth: 0,
        display: 'grid',
        gridTemplateColumns: rowColumns,
        gap: 12,
        alignItems: 'center',
        padding: '10px 20px 10px 12px',
        textDecoration: 'none',
        color: color.text,
        fontSize: 13,
      }}
    >
      <span style={{ ...cell, fontFamily: font.mono, ...muted }}>{t.ref}</span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <span style={{ display: 'flex', gap: 6, alignItems: 'center', minWidth: 0 }}>
          <StatusChip tone={chip.tone}>{chip.label}</StatusChip>
          {t.priority !== 'normal' && <PriorityChip p={t.priority} />}
          {t.status === 'on_hold' && <StatusChip tone="unk">On hold</StatusChip>}
          <TierChip t={t} />
          <span style={{ ...cell, fontSize: 14, fontWeight: t.unread > 0 ? 700 : 500 }}>
            {t.subject}
          </span>
          {t.unread > 0 && (
            <Badge
              size="sm"
              color="dark"
              aria-label={`${t.unread} unread`}
              style={{ flexShrink: 0 }}
            >
              {t.unread}
            </Badge>
          )}
        </span>
        {second.length > 0 && <span style={{ ...cell, ...muted }}>{second.join(' · ')}</span>}
        {t.match && <SearchMatch text={t.match} />}
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
        <span style={cell}>{t.customer.name}</span>
        {showReseller && t.reseller && (
          <span aria-label="Reseller" style={{ ...cell, fontSize: 12, ...muted }}>
            {t.reseller.name}
          </span>
        )}
      </span>
      <span style={{ ...cell, ...(t.assignee ? undefined : muted) }}>
        {t.assignee?.name ?? 'Unassigned'}
      </span>
      <span style={{ ...cell, textAlign: 'right', ...muted }}>{formatAgo(t.lastMessageAt)}</span>
    </Link>
  );
}

/** The message text a search matched in a thread. */
function SearchMatch({ text }: { text: string }) {
  return (
    <span
      aria-label="Matching message"
      style={{
        fontSize: 13,
        fontStyle: 'italic',
        color: color.textBody,
        borderLeft: `2px solid ${color.rowRule}`,
        paddingLeft: 8,
        overflowWrap: 'anywhere',
      }}
    >
      {text}
    </span>
  );
}
